import {CompanyInfoFieldsInput, CompanyInfoSection} from '@frontend/shared-api';

export type CompanyInfoFieldName = keyof CompanyInfoFieldsInput;

export type CompanyInfoValues = {
  [Field in CompanyInfoFieldName]-?: NonNullable<CompanyInfoFieldsInput[Field]> | null;
};

export type SectionStatus = 'empty' | 'in-progress' | 'complete';

export type WorkforceCategoryKey = 'salaried' | 'industrial' | 'hr' | 'contingent';

export interface WorkforceCategoryDef {
  key: WorkforceCategoryKey;
  labelKey: string;
  headcountFullTime: CompanyInfoFieldName;
  headcountPartTime: CompanyInfoFieldName;
  fte: CompanyInfoFieldName;
}

export interface SectionDef {
  name: CompanyInfoSection;
  titleKey: string;
  selectFields: CompanyInfoFieldName[];
  categories: WorkforceCategoryDef[];
  countFields: CompanyInfoFieldName[];
}

export interface Office {
  id: number;
  name: string;
  countryCode: string;
  cityName: string;
}

export interface UnitSummary {
  office: Office;
  values: CompanyInfoValues;
  status: SectionStatus;
}

export interface WorkforceSplit {
  total: number;
  salaried: number;
  industrial: number;
}

export interface UnitKpis {
  headcount: WorkforceSplit;
  fte: WorkforceSplit;
}

export const GERMANY_COUNTRY_CODE = 'DE';

export const SALARIED_CATEGORY: WorkforceCategoryDef = {
  key: 'salaried',
  labelKey: 'project.steps.companyInfo.categorySalaried',
  headcountFullTime: 'salariedHeadcountFullTime',
  headcountPartTime: 'salariedHeadcountPartTime',
  fte: 'salariedFte',
};

export const INDUSTRIAL_CATEGORY: WorkforceCategoryDef = {
  key: 'industrial',
  labelKey: 'project.steps.companyInfo.categoryIndustrial',
  headcountFullTime: 'industrialHeadcountFullTime',
  headcountPartTime: 'industrialHeadcountPartTime',
  fte: 'industrialFte',
};

export const HR_CATEGORY: WorkforceCategoryDef = {
  key: 'hr',
  labelKey: 'project.steps.companyInfo.categoryHr',
  headcountFullTime: 'hrHeadcountFullTime',
  headcountPartTime: 'hrHeadcountPartTime',
  fte: 'hrFte',
};

export const CONTINGENT_CATEGORY: WorkforceCategoryDef = {
  key: 'contingent',
  labelKey: 'project.steps.companyInfo.categoryContingent',
  headcountFullTime: 'contingentHeadcountFullTime',
  headcountPartTime: 'contingentHeadcountPartTime',
  fte: 'contingentFte',
};

export const SECTION_DEFS: SectionDef[] = [
  {
    name: CompanyInfoSection.INDUSTRY,
    titleKey: 'project.steps.industryGroup',
    selectFields: ['industryId', 'tariffId'],
    categories: [],
    countFields: [],
  },
  {
    name: CompanyInfoSection.EMPLOYEE_STRUCTURE,
    titleKey: 'project.steps.employeeStructureGroup',
    selectFields: [],
    categories: [SALARIED_CATEGORY, INDUSTRIAL_CATEGORY],
    countFields: [],
  },
  {
    name: CompanyInfoSection.HR_EMPLOYEES,
    titleKey: 'project.steps.hrEmployeesGroup',
    selectFields: [],
    categories: [HR_CATEGORY],
    countFields: [],
  },
  {
    name: CompanyInfoSection.CONTINGENT_WORKERS,
    titleKey: 'project.steps.contingentGroup',
    selectFields: [],
    categories: [CONTINGENT_CATEGORY],
    countFields: [],
  },
  {
    name: CompanyInfoSection.OTHER_EMPLOYEES,
    titleKey: 'project.steps.otherGroup',
    selectFields: [],
    categories: [],
    countFields: ['pensioners', 'inactiveEmployees'],
  },
];

export const EMPTY_COMPANY_INFO_VALUES: CompanyInfoValues = {
  industryId: null,
  tariffId: null,
  salariedHeadcountFullTime: null,
  salariedHeadcountPartTime: null,
  salariedFte: null,
  industrialHeadcountFullTime: null,
  industrialHeadcountPartTime: null,
  industrialFte: null,
  hrHeadcountFullTime: null,
  hrHeadcountPartTime: null,
  hrFte: null,
  contingentHeadcountFullTime: null,
  contingentHeadcountPartTime: null,
  contingentFte: null,
  pensioners: null,
  inactiveEmployees: null,
};
